//================================================================
//  Component: Add Commercial Task Pane 
//================================================================

//  Purpose: This side pane allows business partners to create commercial requests

//  Properties:
//    - paneOpen = { useState, holds a boolean value that opens and closes the sidePane}
//    - setPaneOpen = { useState, updates the boolean value that opens and closes the sidePane}
//    - task = { useState, store the related parent demand management task}

//  Example:
//  
//    <AddCommercialTaskPane
//      paneOpen={paneOpen}
//      setPaneOpen={setPaneOpen}
//      task={task}
//    ></AddCommercialTaskPane>

//================================================================

//Libraries
import React, { useContext, useState, useReducer, useEffect } from 'react';
import { useForm } from 'react-hook-form';

//Contexts
import { GetUser } from '../../../../../Library/GlobalContexts';

//Components
import SelectVendorField from '../SelectVendorField';
import UserSearchRHF from '../../../../../Components/UserSearchRHF/UserSearchRHF';
import PTELookupRHF from '../../../../../Components/PTELookupRHF/PTELookupRHF';

//Functions
import WriteDocument from '../../../../../Library/WriteDocument';
import UploadFile from '../../../../../Library/UploadFile';
import CreatePoANumber from '../../../../../Library/CreatePoANumber';

//Images
import FileIcon from '../../../../../Components/Images/Icon_File_Black.svg'
import Loading from '../../../../../Components/Images/Image_Loading_Ripple.svg';

//CSS


export default function AddCommercialTaskPane({
  paneOpen,
  setPaneOpen,
  task,
}) {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  // --------------------------------------------------
  //  Form State
  // --------------------------------------------------

  const { register, handleSubmit, setValue, reset, watch, clearErrors, setError, formState: { errors } } = useForm();

  //------------------------------------------------------
  //  useState
  //------------------------------------------------------

  // Used to set pane status > 'onload', 'pending', 'error'
  const [paneStatus, setPaneStatus] = useState('onload');

  //------------------------------------------------------
  //  useReducer
  //------------------------------------------------------

  // Used to store form data not saved in react hook forms
  const [formData, setFormData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      'supportingDocs': [],
      'supportingDocsError': '',
    }
  );

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Submit Handler 
  const onSubmit = (data) => {

    //------------------------------------------------------
    //  Validation Checks
    //------------------------------------------------------

    let preventSubmit = false;

    // Check User Selected for All Required User Search Fields
    //  - Sponsor Name
    if (data?.sponsornamegivenname === undefined || data?.sponsornamegivenname === '') {
      preventSubmit = true;
      setError('sponsorname', {
          type: 'required',
          message: 'This field is required.'
      })
    }

    if (data.sponsornamesurname === undefined || data.sponsornamesurname === '') {
      preventSubmit = true;
      setError('sponsorname', {
          type: 'required',
          message: 'This field is required.'
      })
    }

    // Check the Vendor Lookup is populated
    // - New Vendor > Check all details are there
    if (data.newvendorname?.length > 0 && (data.newvendortradingname === undefined || data.newvendortradingname === '')) {
      preventSubmit = true;
      setError('newvendorname', {
          type: 'required',
          message: 'This field is required.'
      });

      setError('newvendortradingname', {
        type: 'required',
        message: 'This field is required.'
      });
    }

    // Existing Vendor > Check all details are there
    if (data.vendorname?.length > 0 && (data['vendorname-vendorid'] === undefined || data['vendorname-vendorid'] === '')) {
      preventSubmit = true;
      setError('vendorname', {
          type: 'required',
          message: 'This field is required.'
      });
    }

    // Check if ALL validations have passed
    if (preventSubmit === true) return;

    //------------------------------------------------------
    //  Write Document
    //------------------------------------------------------
    
    setPaneStatus('pending');

    // Generate the PoA Number 
    return CreatePoANumber()
    .then((poaNumber) => {

      const filePromises = [];

      // Check if attachment exists > Upload File
      formData.supportingDocs.forEach((document) => {

        filePromises.push(
          UploadFile(`requests/${poaNumber[0]}/${document?.fileId}`, document?.fileObject)
        );

      })

      // ----------------------------------------------
      //  Settle Promises > Write to Firestore
      // ----------------------------------------------

      return Promise.all(filePromises)
        .then((urls) => {

          const updatedDocs = [];

          // Loop through docs > Add URL
          formData?.supportingDocs.forEach((document, index) => {
            updatedDocs.push({
              'fileUrl': urls[index],
              'fileId': document?.fileId,
              'fileName': document?.fileName,
              'fileType': document?.fileType
            })
          })

          const commercialTask = {
            'parentrequestid': task?.parentrequestid,
            'requestorigin': 'Demand',
            'tasktype': 'Commercial Review',
            'taskname': 'Demand Commercial Request',
            'taskdescription': `${data?.requesttype} for ${data?.applicationname}`,
            'applicationname': data?.applicationname,
            'initiativename': task?.initiativename,
            'stage': 'Triage',
            'status': 'Not Started',
            'assignmentgroup': 'Commercial',
            'requesttype': data?.requesttype,
            'activityfeedvisible': true,
            'approvalhubvisible': false,
            'region': data?.region,
            'assignedto': {
              'email': 'Unassigned',
              'givenname': '',
              'surname': ''
            },
            'assigneddate': null,
            'createddate': new Date(),
            'createdby': {
              'email': getUser?.emailaddress,
              'givenname': getUser?.givenname,
              'surname': getUser?.surname
            },
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
              'email': getUser?.emailaddress,
              'givenname': getUser?.givenname,
              'surname': getUser?.surname
            },
            'activityfeed': [
              {
                'activitydate': new Date(),
                'actionedby': {
                  'email': getUser?.emailaddress,
                  'givenname': getUser?.givenname,
                  'surname': getUser?.surname
                },
                'action': 'created',
                'comments': '',
              }
            ],

            // Commercial Fields
            'requestername': data?.requestername,
            'supportingdocs': updatedDocs,
            'shortdescription': data?.shortdescription,
            'commercialscope': data?.commercialscope,
            'sponsorname': {
              'email': data?.sponsorname,
              'givenname': data?.sponsornamegivenname,
              'surname': data?.sponsornamesurname
            },
            'buysellagreement': data?.buysellagreement,
            'agreementtype': data?.agreementtype,
            'lendleasecompany': data?.lendleasecompany,
            'vendor': {
              'vendorname': data?.vendorname === undefined ? '' : data?.vendorname,
              'tradingas': data?.vendorname ? data['vendorname-tradingas'] : '',
              'vendorid': data?.vendorname ? data['vendorname-vendorid'] : ''
            },
            'newvendorname': data?.newvendorname === undefined ? '' : data?.newvendorname,
            'newvendorcontactphone': data?.newvendorphone === undefined ? '' : data?.newvendorphone,
            'newvendorcontactemail': data?.newvendoremail === undefined ? '' : data?.newvendoremail,
            'newvendortradingname': data?.newvendortradingname === undefined ? '' : data?.newvendortradingname,
            'currency': data?.currency,
            'estannualcontractvalue': data?.estannualcontractvalue,
            'esttotalcontractvalue': data?.esttotalcontractvalue,
            'projectcode': data?.projectcode,
            'tasknumber': data?.tasknumber,
            'expendituretype': data?.expendituretype,
            'secondroundapprovals': [],
            'signedcontracts': [],
            'agreement': {
              'vendor': {
                'vendorname': '',
                'vendorid': ''
              },
              'agreementstartdate': null,
              'agreementenddate': null,
              'currency': '',
              'totalagreementamount': '',
              'autorenewals': '',
              'noticeperiodforautorenewaltermination': {},
              'extensionagreement': '',
              'noticeperiodforextension': {},
              'executiondate': null
            },
            'poraised': true, // By default, this is true
            'potask': {
              'shortdescription': `Raise PO for ${data?.applicationname}`,
              'commercialscope': `Raise PO for ${data?.applicationname}`
            },
            'comments': [],
            'urgency': data?.urgency,
            'impact': data?.impact,
          }

          return WriteDocument('requests', poaNumber[0], commercialTask, true)
            .then(() => {

              // Reset Form and Close Pane
              reset();
              setFormData({
                'supportingDocs': []
              });
              setPaneStatus('onload');
              setPaneOpen({ 'addCommercial': false });

            })

        })

    })
    .catch((error) => {

      console.log(error)
      setPaneStatus('error');

    })

  }

  //------------------------------------------------------
  //  useEffect
  //------------------------------------------------------

  // Set all the default values on the form
  useEffect(() => {

    if (task === undefined) return;

    setValue('applicationname', task?.applicationname);
    setValue('projectcode', task?.projectcode);
    setValue('tasknumber', task?.tasknumber);
    setValue('expendituretype', task?.expendituretype);
    setValue('requestername', task?.createdby?.email);
    setValue('sponsorname', task?.initialbuapprover?.email);
    setValue('sponsornamegivenname', task?.initialbuapprover?.givenname);
    setValue('sponsornamesurname', task?.initialbuapprover?.surname);

    // eslint-disable-next-line
  }, [task, paneOpen])

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  // ==================================================
  //  Hide Side Pane
  // ==================================================

  if (!paneOpen?.addCommercial) return null;

  // ==================================================
  //  Show Side Pane  
  // ==================================================
  // ---------------------------------------------------
  //  Onload
  // ---------------------------------------------------

  if (paneStatus === 'onload') {

    return (
      <div className='Pane-Background'>
        <dialog className='Pane-Container'>

          {/* Form */}
          <form className='grid grid-rows-[100px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={handleSubmit(onSubmit)}>

            {/* Header */}
            <div className='flex flex-col gap-2 p-4'>
              <h4 className='text-[22px] p-0 m-0'> Demand Commercial Request</h4>
              <p className='m-0'>This request will be sent to the Commercial team after submission.</p>
            </div>

            {/* Body */}
            <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 border border-solid shadow-inner'>
              <div className='Form'>

                {/* ------------- REQUEST INFORMATION ------------- */}

                <h5 className='text-[18px] text-teal-600 mt-2 mb-1'>Request Information</h5>

                {/* =========================================================== */}
                {/*  1. Request Type                                            */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 1. Request Type <span className='text-[#C4314B]'>*</span></label>
                  <select
                    className={errors.requesttype ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                    {...register('requesttype', {
                      required: 'This field is required'
                    })}
                    style={{ width: '300px' }}
                  >
                    <option hidden value=''>-</option>
                    <option value='Establish New Agreement'> Establish New Agreement </option>
                    <option value='Vary/Extend/Renew Existing Agreement'> Vary/Extend/Renew Existing Agreement </option>
                    <option value='Terminate Existing Agreement'> Terminate Existing Agreement </option>

                  </select>

                  {errors.requesttype && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.requesttype?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  2. Application Name                                        */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 2. Application Name <span className='text-[#C4314B]'>*</span></label>
                  <input
                    className={errors.applicationname ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                    style={{ width: '300px' }}
                    {...register('applicationname', {
                      required: 'This field is required'
                    })}
                    placeholder='Enter application name'
                    type='text'
                  ></input>

                  {errors.applicationname && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.applicationname?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  3. Short Description                                       */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 3. Short Description <span className='text-[#C4314B]'>*</span></label>
                  <textarea
                    className={errors.shortdescription ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                    {...register('shortdescription', {
                      required: 'This field is required'
                    })}
                    placeholder='Enter a description'
                  ></textarea>

                  {errors.shortdescription && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.shortdescription?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  4. Requester Name                                          */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 4. Requester Name <span className='text-[#C4314B]'>*</span></label>
                  <div className='w-[400px]'>

                    <UserSearchRHF
                      dataField={'requestername'}
                      required={true}
                      register={register}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      setError={setError}
                      errors={errors.businessowner}
                    ></UserSearchRHF>

                  </div>
                </div>

                {/* =========================================================== */}
                {/*  5. Region                                                  */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 5. Region <span className='text-[#C4314B]'>*</span></label>
                  <select
                    className={errors.region ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                    {...register('region', {
                      required: 'This field is required'
                    })}
                    style={{ width: '300px' }}
                  >
                    <option hidden value=''>-</option>
                    <option value='AMER'> AMER </option>
                    <option value='ASIA'> ASIA </option>
                    <option value='AUST'> AUST </option>
                    <option value='EUR'> EUR </option>
                    <option value='Global'> Global </option>
                  </select>

                  {errors.region && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.region?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/* 6. Sponsor Name                                             */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 6. Sponsor Name <span className='text-[#C4314B]'>*</span></label>
                  <div className='w-[400px]'>
                    <UserSearchRHF
                      dataField={'sponsorname'}
                      required={true}
                      register={register}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      setError={setError}
                      errors={errors.sponsorname}
                    ></UserSearchRHF>
                  </div>

                </div>

                {/* =========================================================== */}
                {/*  7. Supporting Documents                                    */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 7. Supporting Documents </label>

                  {/* File Input */}
                  <div className='Attach-File-Onload-Container'>

                    <input
                      className={formData?.supportingDocsError?.length === 0 ? ('Input-Field-Text') : ('Input-Field-Text-Error')}
                      style={{ padding: '0px' }}
                      type='file'
                      accept='.pdf,.doc,.txt,.xlsx,.docx,.csv,.png,.jpg'
                      onChange={(event) => {

                        const file = event.target.files[0];

                        if (file.size > 20000000) {
                          setFormData({
                            'supportingDocsError': 'File size too large. Please upload a file smaller than 20MB.'
                          });
                          return;
                        }

                        //Set File Id
                        const fileType = file?.name?.split('.')[1];
                        const fileId = `${Date.now().toString()}${Math.floor(Math.random() * (99999 - 10000 + 1) + 10000)}.${fileType}`;

                        // Validate File Types
                        const allowedFileTypes = ['pdf', 'doc', 'txt', 'xlsx', 'docx', 'csv', 'png', 'jpg'];

                        if (allowedFileTypes.includes(fileType.toLowerCase()) === false) {
                          return setFormData({ 'supportingDocsError': `Please upload a file in the following format(s): ${allowedFileTypes.join(', ')}.` });
                        }

                        setFormData({
                          'supportingDocsError': '',
                          'supportingDocs': [
                            ...formData.supportingDocs,
                            {
                              'fileId': fileId,
                              'fileName': file.name,
                              'fileObject': file,
                              'fileType': fileType,
                            }
                          ]
                        });

                        // Clear File Input
                        event.target.value = '';

                      }}
                    ></input>
                  </div>

                  {/* Uploads */}
                  <div className='w-full flex flex-col rounded-md border border-[#D8D8D8]' hidden={formData.supportingDocs.length > 0 ? false : true}>
                    {
                      formData?.supportingDocs?.map((file, index) => (
                        <div key={index} className='flex flex-row justify-between items-center border-b border-b-[#D8D8D8] last:border-0 px-3 py-1'>

                          {/* File Name */}
                          <div className='flex flex-row gap-2 items-center'>
                            <img src={FileIcon} alt='File'></img>
                            {file?.fileName}
                          </div>

                          {/* Delete Button */}
                          <div className='Cancel-Icon cursor-pointer mt-1' onClick={() => {

                            setFormData({
                              'supportingDocs': formData.supportingDocs.filter(item => item !== file)
                            });

                          }
                          }></div>

                        </div>
                      ))
                    }
                  </div>

                  {/* Error Message */}
                  <label className='font-medium text-[#DE0000]' hidden={formData?.supportingDocsError.length === 0}>{formData?.supportingDocsError}</label>

                </div>

                {/* ------------- CONTRACT INFORMATION ------------- */}

                <h5 className='text-[18px] text-teal-600 mt-4 mb-2'>Contract Information</h5>

                {/* =========================================================== */}
                {/*  8. Detailed Request / Scope Description                    */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 8. Detailed Request / Scope Description <span className='text-[#C4314B]'>*</span></label>
                  <textarea
                    className={errors.commercialscope ? ('Input-Field-TextArea-Error') : ('Input-Field-TextArea')}
                    {...register('commercialscope', {
                      required: 'This field is required'
                    })}
                    placeholder='Describe the scope of this request'
                  ></textarea>

                  {errors.commercialscope && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.commercialscope?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  9. Urgency                                                 */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 9. Urgency <span className='text-[#C4314B]'>*</span></label>
                  <select
                    className={errors.urgency ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                    {...register('urgency', {
                      required: 'This field is required'
                    })}
                    style={{ width: '300px' }}
                  >
                    <option hidden value=''>-</option>
                    <option value='Low'> Low </option>
                    <option value='Medium'> Medium </option>
                    <option value='High'> High </option>
                  </select>

                  {errors.urgency && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.urgency?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  10. Impact                                                 */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 10. Impact <span className='text-[#C4314B]'>*</span></label>
                  <select
                    className={errors.impact ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                    {...register('impact', {
                      required: 'This field is required'
                    })}
                    style={{ width: '300px' }}
                  >
                    <option hidden value=''>-</option>
                    <option value='Low'> Low </option>
                    <option value='Medium'> Medium </option>
                    <option value='High'> High </option>
                  </select>

                  {errors.impact && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.impact?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  11. Buy / Sell Agreement                                   */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 11. Is this a Buy or Sell Agreement? <span className='text-[#C4314B]'>*</span></label>
                  <select
                    className={errors.buysellagreement ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                    {...register('buysellagreement', {
                      required: 'This field is required'
                    })}
                    style={{ width: '300px' }}
                  >
                    <option hidden value=''>-</option>
                    <option value='Buy'> Buy </option>
                    <option value='Sell'> Sell </option>
                    <option value='N/A'> N/A </option>
                  </select>

                  {errors.buysellagreement && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.buysellagreement?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  12. Agreement Type                                         */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 12. Agreement Type <span className='text-[#C4314B]'>*</span></label>
                  <select
                    className={errors.agreementtype ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                    {...register('agreementtype', {
                      required: 'This field is required'
                    })}
                    style={{ width: '300px' }}
                  >
                    <option hidden value=''>-</option>
                    <option value='Consultancy Services Agreement (CSA)'> Consultancy Services Agreement (CSA) </option>
                    <option value='Hardware Order'> Hardware Order </option>
                    <option value='Letter of Intent (LoI)'> Letter of Intent (LoI) </option>
                    <option value='Master Services Agreement (MSA)'> Master Services Agreement (MSA) </option>
                    <option value='Non-Disclosure Agreement (NDA)'> Non-Disclosure Agreement (NDA) </option>
                    <option value='Novation/Assignment Agreement'> Novation/Assignment Agreement </option>
                    <option value='Service Order (SO)'> Service Order (SO) </option>
                    <option value='Software License'> Software License </option>
                    <option value='Subscription'> Subscription </option>
                    <option value='Supply Agreement'> Supply Agreement </option>
                    <option value='Variation'> Variation </option>
                  </select>

                  {errors.agreementtype && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.agreementtype?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  13. Lendlease Company                                      */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 13. Lendlease Company (Signing Entity) <span className='text-[#C4314B]'>*</span></label>
                  <select
                    className={errors.lendleasecompany ? 'Input-Field-Select-Error' : 'Input-Field-Select'}
                    style={{ width: '300px' }}
                    {...register('lendleasecompany', {
                      required: 'This field is required'
                    })}
                  >
                    <option hidden value=''>-</option>
                    <option value='TBC'>TBC</option>
                    <option value='Lendlease Asia Holdings Pte. Ltd'>Lendlease Asia Holdings Pte. Ltd</option>
                    <option value='Lendlease Building Pty Ltd'>Lendlease Building Pty Ltd</option>
                    <option value='Lendlease Chilled Water (Barangaroo South) Pty Limited'>Lendlease Chilled Water (Barangaroo South) Pty Limited</option>
                    <option value='Lendlease Construction Pty Limited'>Lendlease Construction Pty Limited</option>
                    <option value='Lendlease Design Build'>Lendlease Design Build</option>
                    <option value='Lendlease Development Pty Ltd'>Lendlease Development Pty Ltd</option>
                    <option value='Lendlease Digital Asia Pte Ltd'>Lendlease Digital Asia Pte Ltd</option>
                    <option value='Lendlease Digital Australia Holdings Pty Limited'>Lendlease Digital Australia Holdings Pty Limited</option>
                    <option value='Lendlease Digital Australia Pty Ltd'>Lendlease Digital Australia Pty Ltd</option>
                    <option value='Lendlease Digital Investments Pty Limited'>Lendlease Digital Investments Pty Limited</option>
                    <option value='Lendlease Digital US Holdings LLC'>Lendlease Digital US Holdings LLC</option>
                    <option value='Lendlease Digital US LLC'>Lendlease Digital US LLC</option>
                    <option value='Lendlease Embedded Network (Barangaroo South) Pty Limited'>Lendlease Embedded Network (Barangaroo South) Pty Limited</option>
                    <option value='Lendlease Engineering Pty Ltd'>Lendlease Engineering Pty Ltd</option>
                    <option value='Lendlease Europe Limited'>Lendlease Europe Limited</option>
                    <option value='Lendlease Global Commercial Trust Management Pte Ltd'>Lendlease Global Commercial Trust Management Pte Ltd</option>
                    <option value='Lendlease Group Services Pty Limited'>Lendlease Group Services Pty Limited</option>
                    <option value='Lendlease ICT Services, Inc'>Lendlease ICT Services, Inc</option>
                    <option value='Lendlease Management Services Limited'>Lendlease Management Services Limited</option>
                    <option value='Lendlease Project Management & Construction (Shanghai) Co Ltd'>Lendlease Project Management & Construction (Shanghai) Co Ltd</option>
                    <option value='Lendlease Recycled Water (Barangaroo South) Pty Limited'>Lendlease Recycled Water (Barangaroo South) Pty Limited</option>
                    <option value='Lendlease Retirement Living Holding Pty Limited'>Lendlease Retirement Living Holding Pty Limited</option>
                    <option value='Lendlease Services Pty Limited'>Lendlease Services Pty Limited</option>
                    <option value='Lendlease Singapore Pte Ltd'>Lendlease Singapore Pte Ltd</option>
                    <option value='Lendlease Technology Delivery Pty Limited'>Lendlease Technology Delivery Pty Limited</option>
                    <option value='Lendlease Technology Pty Ltd'>Lendlease Technology Pty Ltd</option>
                    <option value='Podium Asset Holdings Pty Limited'>Podium Asset Holdings Pty Limited</option>
                    <option value='Podium Property Services LLC'>Podium Property Services LLC</option>
                    <option value='Lendlease (Elephant & Castle) Ltd'>Lendlease (Elephant & Castle) Ltd</option>
                    <option value='Lendlease (US) Construction Inc'>Lendlease (US) Construction Inc</option>
                    <option value='Lendlease (US) Construction, LMB Inc'>Lendlease (US) Construction, LMB Inc</option>
                    <option value='Lendlease (US) Development'>Lendlease (US) Development</option>
                    <option value='Lendlease (US) Partnerships'>Lendlease (US) Partnerships</option>
                    <option value='Lendlease (US) Services Inc'>Lendlease (US) Services Inc</option>
                  </select>
                  {errors.lendleasecompany && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.lendleasecompany?.message}</p>}
                </div>

                {/* ------------- OTHER PARTY INFORMATION (VENDOR/CUSTOMER) ------------- */}

                <h5 className='text-[18px] text-teal-600 mt-4 mb-2'> Other Party Information (Vendor / Customer) </h5>

                {/* =========================================================== */}
                {/*  14. Vendor Name                                            */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 14. Vendor Name <span className='text-[#C4314B]'>*</span></label>
                  <SelectVendorField
                    register={register}
                    setValue={setValue}
                    errors={errors}
                    clearErrors={clearErrors}
                    setError={setError}
                  ></SelectVendorField>

                </div>

                {/* ------------- FINANCIAL INFORMATION ------------- */}

                <h5 className='text-[18px] text-teal-600 mt-4 mb-2'> Financial Information </h5>

                {/* =========================================================== */}
                {/*  15. Currency                                               */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 15. Currency </label>
                  <select
                    className={errors.currency ? ('Input-Field-Select-Error') : ('Input-Field-Select')}
                    {...register('currency')}
                    style={{ width: '300px' }}
                  >
                    <option hidden value=''>-</option>
                    <option value='AUD'> AUD </option>
                    <option value='SGD'> SGD </option>
                    <option value='GBP'> GBP </option>
                    <option value='USD'> USD </option>
                    <option value='EUR'> EUR </option>
                    <option value='MYR'> MYR </option>
                  </select>

                  {errors.currency && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.currency?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  16. Estimated Annual Contract Value (exGST)                 */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 16. Estimated Annual Contract Value (exGST) </label>
                  <input
                    className={errors.estannualcontractvalue ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                    style={{ width: '300px' }}
                    {...register('estannualcontractvalue', {
                      pattern: {
                        value: /^[-]?\d+(\.\d{1,2})?$/,
                        message: 'Only numbers allowed (up to 2 decimal places)'
                      }
                    })}
                    placeholder='Enter contract value'
                    type='text'
                  ></input>

                  {errors.estannualcontractvalue && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.estannualcontractvalue?.message}</p>}
                </div>

                {/* =========================================================== */}
                {/*  17. Estimated Total Contract Value (exGST)                  */}
                {/* =========================================================== */}

                <div className='Form-Row'>
                  <label className='font-medium'> 17. Estimated Total Contract Value (exGST) </label>
                  <input
                    className={errors.esttotalcontractvalue ? ('Input-Field-Text-Error') : ('Input-Field-Text')}
                    style={{ width: '300px' }}
                    {...register('esttotalcontractvalue', {
                      pattern: {
                        value: /^[-]?\d+(\.\d{1,2})?$/,
                        message: 'Only numbers allowed (up to 2 decimal places)'
                      }
                    })}
                    placeholder='Enter contract value'
                    type='text'
                  ></input>

                  {errors.esttotalcontractvalue && <p className='font-medium text-sm text-[#DE0000] my-2'>{errors.esttotalcontractvalue?.message}</p>}
                </div>

                {/* ============================ */}
                {/*  18. Recharge Information *  */}
                {/* ============================ */}

                <div className='Form-Row'>
                  <label><b style={{ fontWeight: '500' }}> 18. Recharge Information </b> </label>

                  <i className='mb-3'>Project code, task number and expenditure type allow the recharge of technology costs.</i>

                  <PTELookupRHF
                    required={false}
                    register={register}
                    setValue={setValue}
                    errors={errors}
                    clearErrors={clearErrors}
                    disabled={false}
                    watch={watch}
                  ></PTELookupRHF>

                </div>

              </div>
            </div>

            {/* Footer */}
            <div className='px-4 py-3'>
              <div className='flex flex-row gap-2'>
                <button className='Primary-Button' type='submit' value='submit'> Create </button>
                <button className='Secondary-Button' onClick={() => {

                  // Reset Form and Close Side Pane
                  reset();
                  setPaneOpen({ 'addCommercial': false });
                  setFormData({
                    'supportingDocs': []
                  });

                }}> Cancel </button>
              </div>
            </div>

          </form>

        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Pending
  // ---------------------------------------------------

  else if (paneStatus === 'pending') {
    return (
      <div className='Pane-Background'>
        <dialog className='Pane-Container flex justify-center items-center text-center'>
          <img alt='loading-circle-icon' src={Loading}></img>
        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Error
  // ---------------------------------------------------

  else if (paneStatus === 'error') {
    return (
      <div className='Pane-Background'>

        {/* Pane Container */}
        <dialog className='Pane-Container'>
          <div className='flex flex-col gap-2 w-full justify-center items-center'>

            <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
            <h4 className='mb-0'>Oops! Something went wrong.</h4>
            <p className='text-center leading-[1.7]'>
              An error occurred while we processed your request.
            </p>
            <button
              className='Primary-Button'
              onClick={() => {

                // Reset Form and Close Pane
                reset();
                setPaneOpen({ 'addCommercial': false });
                setPaneStatus('onload');
                setFormData({
                  'supportingDocs': []
                });

              }}
            >
              Close
            </button>

          </div>
        </dialog>
      </div>

    )
  }

  //------------------------------------------------------

}